import { roundness } from "@provider/dripsy";
import type { DripsyColor } from "@utils";
import { useDripsyColor } from "@utils";
import { ActivityIndicator, Text, useSx, View } from "dripsy";
import AnimatedPressable from "./animated-pressable";
import * as React from "react";

function Button({
  onPress,
  testID,
  children,
  left,
  right,
  description,
  outline,
  color = "$primary",
  titleColor = "$primary",
  justifyContent = "center",
  px = "$2",
  py = "$2",
  disabled = false,
  isLoading = false,
  spacing = "$2",
  ...rest
}: {
  onPress?: (e: any) => void;
  testID?: string;
  children: string;
  left?: any;
  right?: any;
  description?: string;
  outline?: boolean;
  color?: DripsyColor;
  titleColor?: string;
  justifyContent?: "center" | "flex-start" | "space-between";
  px?: "$1" | "$2" | "$3" | "$4";
  py?: "$1" | "$2" | "$4";
  spacing?: "$1" | "$2";
  disabled?: boolean;
  isLoading?: boolean;
}) {
  const sx = useSx();

  const getPressedColor = (): DripsyColor => {
    if (color === "$primary") {
      return "$primaryPressed";
    }
    if (color === "$error") {
      return "$errorOutlinePressedColor";
    }

    if (color === "$secondaryBackgroundDarker") {
      return "$secondaryBackgroundPressed";
    }
    if (color === "$orange") {
      return "$orangePressed";
    }
    if (color === "$textGrey") {
      return "$grey";
    }
    // console.log("pressed color is not mapped for", color);
    return "$primaryPressed";
  };

  const animatedColor = useDripsyColor({ color });
  const pressedColor = useDripsyColor({ color: getPressedColor() });

  const [height, setHeight] = React.useState(0);

  return (
    <AnimatedPressable
      {...rest}
      testID={testID}
      onPress={isLoading ? () => {} : (onPress as any)}
      onLayout={(layout) => {
        const newHeight = layout.nativeEvent.layout.height;
        if (height === 0 || height < newHeight) {
          setHeight(newHeight);
        }
      }}
      animate={React.useMemo(
        () =>
          ({ hovered, pressed }) => {
            if (disabled) {
              // Disabled state - use same color but with opacity
              if (outline) {
                return {
                  backgroundColor: "transparent",
                  borderColor: animatedColor,
                  opacity: 0.5,
                };
              }
              return {
                backgroundColor: animatedColor,
                borderColor: animatedColor,
                opacity: 0.5,
              };
            }
            if (outline) {
              return {
                backgroundColor: "transparent",
                borderColor: hovered || pressed ? pressedColor : animatedColor,
                opacity: 1,
              };
            }
            return {
              backgroundColor: hovered || pressed ? pressedColor : animatedColor,
              borderColor: hovered || pressed ? pressedColor : animatedColor,
              opacity: 1,
            };
          },
        [pressedColor, animatedColor, disabled],
      )}
      style={sx({
        borderWidth: 2,
        // backgroundColor: outline ? "transparent" : color,
        py,
        px,
        borderRadius: roundness,
        alignItems: "center",
        flexDirection: "row",
        justifyContent,
        height: height > 0 ? height : "auto",
      })}
    >
      {isLoading ? (
        <ActivityIndicator color={outline ? "$primary" : "$background"} />
      ) : (
        <>
          {left ? <View sx={{ marginRight: spacing }}>{left}</View> : null}
          <View style={{ alignItems: "flex-start" }}>
            <Text
              selectable={false}
              sx={{
                color: outline ? titleColor : "$background",
                fontWeight: "600",
                fontSize: [14, 16],
                letterSpacing: 0.4,
                opacity: disabled ? 0.7 : 1,
              }}
            >
              {children}
            </Text>
            {description ? (
              <Text
                selectable={false}
                sx={{
                  color: outline ? "$primary" : "$background",
                  fontWeight: "600",
                  fontSize: 12,
                  // lineHeight: 12,
                  opacity: 0.7,
                }}
              >
                {description}
              </Text>
            ) : null}
          </View>
          {right ? <View sx={{ marginLeft: spacing }}>{right}</View> : null}
        </>
      )}
    </AnimatedPressable>
  );
}
export default Button;
